import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { TrimRoomState } from '@trimlog/trim-room-contract';
import { executeCommand, fetchTrimRoom } from '../../lib/trimRoom';
import { useAuth } from '../../hooks/useAuth';
import { useUnits } from '../../hooks/useUnits';
import { parseWeightInput, weightEntryUnit } from '../../lib/units';
import { uuid } from '../../lib/uuid';
import { ApiError } from '../../lib/api';
import { Modal } from '../Modal';

export type AssignedBag = { id: string; label: string; batchId: string; strainId: number; strainName: string };
// No stock figure here on purpose: the material layer zeroes available weight
// for non-managers, so workers see which batches exist, not how much is left.
// Over-taking is refused server-side (insufficient_stock) with a clear message.
type OpenBatch = { id: string; lot: string; room: string; strainId: number; strainName: string; revision: number };
type Assigned = { bags: AssignedBag[]; open: OpenBatch[] };

// The worker's link between "the batch the manager set up" and "the trim I'm
// about to do". Two ways in:
//   1. Bags a manager already weighed out and issued to this worker.
//   2. Any OPEN batch: the worker weighs a bag out for themselves (bag.take).
//      Before this, a batch with no manager-issued bags was invisible here, so
//      a freshly created batch could never be trimmed from.
// Every bag — issued or self-taken — is still weighed back in and reviewed by
// a manager on return, so the batch's material balance stays whole.
export function BagAssignment({ onChoose }: { onChoose: (bag: AssignedBag) => void }) {
  const { orgId, player } = useAuth();
  const units = useUnits();
  const queryClient = useQueryClient();
  const key = `trim:assigned:${orgId}:${player?.id}`;
  const chosen = useRef(false);
  const [taking, setTaking] = useState<OpenBatch | null>(null);
  const [amount, setAmount] = useState('');
  const [tare, setTare] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const { data } = useQuery({
    queryKey: ['assigned-bags', orgId, player?.id], enabled: !!player,
    queryFn: async (): Promise<Assigned> => {
      try {
        const state: TrimRoomState = await fetchTrimRoom();
        if (!state.settings.enabled) return { bags: [], open: [] };
        const strainName = (id: number) => state.strains.find(s => s.id === id)?.name || 'Assigned cultivar';
        const bags = state.bags.filter(b => b.assignedPlayerId === player?.id && b.state === 'issued').map(b => {
          const batch = state.batches.find(x => x.id === b.batchId)!;
          return { id: b.id, label: b.label, batchId: b.batchId, strainId: batch.strainId, strainName: strainName(batch.strainId) };
        });
        const open = state.batches.filter(b => b.state === 'open').map(b => ({
          id: b.id, lot: b.lotReference, room: b.roomReference, strainId: b.strainId,
          strainName: strainName(b.strainId), revision: b.revision,
        }));
        const result = { bags, open };
        localStorage.setItem(key, JSON.stringify(result));
        return result;
      } catch (error) {
        if (navigator.onLine) throw error;
        // Offline: show what was cached. Taking a new bag needs a connection
        // (it moves stock), so cached open batches are shown but disabled.
        const cached = JSON.parse(localStorage.getItem(key) || '{}') as Partial<Assigned> | AssignedBag[];
        return Array.isArray(cached) ? { bags: cached, open: [] } : { bags: cached.bags ?? [], open: cached.open ?? [] };
      }
    }, retry: false, networkMode: 'always',
  });

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('bag');
    const bag = data?.bags.find(b => b.id === requested || b.label === requested);
    if (bag && !chosen.current) { chosen.current = true; onChoose(bag); }
  }, [data]);

  async function takeBag() {
    if (!taking || !player) return;
    const grossG = parseWeightInput(amount, units);
    const tareG = tare.trim() ? parseWeightInput(tare, units) : 0;
    const netG = grossG - tareG;
    if (!Number.isFinite(grossG) || grossG <= 0) { setError('Enter what the bag weighs on the scale.'); return; }
    if (!Number.isFinite(tareG) || tareG < 0 || netG <= 0) { setError('The empty-bag weight has to be less than the full weight.'); return; }
    setBusy(true); setError('');
    const bagId = uuid();
    const stamp = new Date().toTimeString().slice(0, 5).replace(':', '');
    const label = `${taking.lot}-${player.name}-${stamp}-${bagId.slice(0, 4)}`;
    try {
      await executeCommand({
        commandId: uuid(), type: 'bag.take', bagId, batchId: taking.id, label,
        expectedRevision: taking.revision,
        // Weighing is recorded in milligrams, the material ledger's unit.
        input: { grossMg: Math.round(grossG * 1000), tareMg: Math.round(tareG * 1000), source: 'manual', measuredAtMs: Date.now() },
      });
      await queryClient.invalidateQueries({ queryKey: ['assigned-bags', orgId, player.id] });
      const bag = { id: bagId, label, batchId: taking.id, strainId: taking.strainId, strainName: taking.strainName };
      setTaking(null); setAmount(''); setTare('');
      onChoose(bag);
    } catch (e) {
      // 409 = another worker took material from this batch a moment ago; the
      // refreshed revision makes the next attempt go through.
      if (e instanceof ApiError && e.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ['assigned-bags', orgId, player.id] });
        setError('Someone else just took from this batch. Check the weight and try again.');
      } else if (e instanceof ApiError && /left in this batch|available stock/i.test(e.message)) {
        setError("That's more than this batch has left. Check the scale, or ask your manager.");
      } else {
        setError(e instanceof Error ? e.message : 'Could not save. Check the connection and retry.');
      }
    } finally { setBusy(false); }
  }

  if (!data || (!data.bags.length && !data.open.length)) return null;
  const unit = weightEntryUnit(units);
  return (
    <div className="panel" data-testid="batch-link">
      {data.bags.length > 0 && <>
        <h2>Your assigned bags</h2>
        <p className="small muted">Choose existing material to link this work. Quick logging stays available below.</p>
        {!navigator.onLine && <p role="status">Cached assignments. Any changes will need review when you reconnect.</p>}
        <div className="btn-row">{data.bags.map(b => (
          <button type="button" className="btn btn-ghost" key={b.id} data-testid={`assigned-bag-${b.label}`} onClick={() => onChoose(b)}>{b.label} · {b.strainName}</button>
        ))}</div>
      </>}
      {data.open.length > 0 && <>
        <h2 style={{ marginTop: data.bags.length ? 14 : 0 }}>Trim from a batch</h2>
        <p className="small muted">Pick the batch you're working on, weigh your bag out, and your trim counts toward it.</p>
        <div className="btn-row">{data.open.map(b => (
          <button
            type="button" className="btn btn-ghost" key={b.id}
            data-testid={`take-batch-${b.lot}`}
            disabled={!navigator.onLine}
            onClick={() => { setTaking(b); setError(''); }}
          >
            {b.lot} · {b.room} · {b.strainName}
          </button>
        ))}</div>
        {!navigator.onLine && <p className="small muted">Reconnect to take a bag — it moves stock out of the batch.</p>}
      </>}

      {taking && (
        <Modal title={`Take a bag from ${taking.lot}`} onClose={() => setTaking(null)}>
          <p className="small muted">{taking.room} · {taking.strainName}</p>
          <div className="field">
            <label className="field-label" htmlFor="take-amount">Bag weight on the scale ({unit})</label>
            <input id="take-amount" data-testid="take-amount" type="number" inputMode="decimal" min="0" step="0.1"
              value={amount} onChange={e => setAmount(e.target.value)} autoFocus />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="take-tare">Empty bag / tote weight ({unit}) — optional</label>
            <input id="take-tare" data-testid="take-tare" type="number" inputMode="decimal" min="0" step="0.1"
              value={tare} onChange={e => setTare(e.target.value)} placeholder="0" />
          </div>
          {error && <p className="small" role="alert" data-testid="take-error">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setTaking(null)}>Cancel</button>
            <button type="button" className="btn btn-primary" data-testid="take-submit" disabled={busy} onClick={() => void takeBag()}>
              {busy ? 'Saving…' : 'Take bag and start'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
